import Axios from 'axios';
import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router';
import {
  FORGOT_PASSWORD_EMAIL_VERIFY,
  FORGOT_PASSWORD_UPDATE,
  CONFIG_PASSWORD_POLICY,
} from '../../config/rest_endpoints';
import { AUTH_ROUTES } from '../../routers/RouteConstants';
import Logo from '../../components/common/Logo';
import './style.css';

const UpdatePassword = () => {
  const history = useHistory();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [matchPasswordError, setMatchPasswordError] = useState(false);
  const [showPassword, setShowPassword] = useState('');
  const [apiToken, setApiToken] = useState('');
  const [isLoad, setIsLoad] = useState(false);
  const [respMessage, setRespMessage] = useState('');
  const [isError, setIsError] = useState(null);
  const [passwordConfig, setPasswordConfig] = useState({
    require_uppercase: false,
    require_lowercase: false,
    require_numbers: false,
    require_specials: false,
    min_length: 0,
  });

  const [validations, setValidations] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false,
  });

  const validatePasswordWithConfig = (password, config) => {
    return {
      length: password.length >= config.min_length,
      uppercase: !config.require_uppercase || /[A-Z]/.test(password),
      lowercase: !config.require_lowercase || /[a-z]/.test(password),
      number: !config.require_numbers || /[0-9]/.test(password),
      special: !config.require_specials || /[!@#$%^&*(),.?":{}|<>]/.test(password),
    };
  };

  const isPasswordValid = validations => {
    return Object.values(validations).every(v => v);
  };

  useEffect(() => {
    const getPasswordConfig = async () => {
      try {
        const res = await Axios.get(CONFIG_PASSWORD_POLICY);
        setPasswordConfig(res?.data?.response?.data || passwordConfig);
      } catch (err) {
        console.log('Error fetching password config:', err);
      }
    };
    getPasswordConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const getTokenUrl = window.location.href.split('t=')[1];
    const headers = { Authorization: 'Bearer ' + getTokenUrl };
    Axios.get(FORGOT_PASSWORD_EMAIL_VERIFY, { headers })
      .then(res => {
        if (res) {
          const { response } = res.data;
          let newToken = response.data.token;
          setApiToken(newToken);
        }
      })
      .catch(err => {
        console.log(`err`, err);
      });
  }, []);

  const handlePassword = e => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    const newValidations = validatePasswordWithConfig(newPassword, passwordConfig);
    setValidations(newValidations);

    if (newPassword !== confirmPassword) {
      setMatchPasswordError(true);
    } else {
      setMatchPasswordError(false);
    }
  };

  const handleConfirmPassword = e => {
    setConfirmPassword(e.target.value);
    if (password !== e.target.value) {
      setMatchPasswordError(true);
    } else {
      setMatchPasswordError(false);
    }
  };

  const clearState = () => {
    setPassword('');
    setConfirmPassword('');
    setValidations({
      length: false,
      uppercase: false,
      lowercase: false,
      number: false,
      special: false,
    });
  };

  const renderPasswordRequirements = () => {
    if (!password) return null;

    return (
      <div
        className="password-requirements mb-1 font-weight-bold"
        style={{ fontSize: '0.8rem', color: '#666' }}
      >
        <div style={{ marginBottom: '0.5rem' }}>Password must contain:</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '0.25rem' }}>
          <div style={{ color: validations.length ? '#4caf50' : 'rgba(255, 0, 0, 0.637)' }}>
            {validations.length ? '✓' : 'x'} At least {passwordConfig.min_length} characters
          </div>
          {passwordConfig.require_uppercase && (
            <div style={{ color: validations.uppercase ? '#4caf50' : 'rgba(255, 0, 0, 0.637)' }}>
              {validations.uppercase ? '✓' : 'x'} One uppercase letter
            </div>
          )}
          {passwordConfig.require_lowercase && (
            <div style={{ color: validations.lowercase ? '#4caf50' : 'rgba(255, 0, 0, 0.637)' }}>
              {validations.lowercase ? '✓' : 'x'} One lowercase letter
            </div>
          )}
          {passwordConfig.require_numbers && (
            <div style={{ color: validations.number ? '#4caf50' : 'rgba(255, 0, 0, 0.637)' }}>
              {validations.number ? '✓' : 'x'} One number
            </div>
          )}
          {passwordConfig.require_specials && (
            <div style={{ color: validations.special ? '#4caf50' : 'rgba(255, 0, 0, 0.637)' }}>
              {validations.special ? '✓' : 'x'} One special character (!@#$%^&*(),.?":{}|&lt;&gt;)
            </div>
          )}
        </div>
      </div>
    );
  };

  const handleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const submitPassword = async e => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setMatchPasswordError(true);
      return;
    }

    try {
      const configResponse = await Axios.get(CONFIG_PASSWORD_POLICY);
      const freshConfig = configResponse?.data?.response?.data;
      if (!freshConfig) {
        setRespMessage('Unable to verify password requirements');
        setIsError(true);
        return;
      }
      const currentValidations = validatePasswordWithConfig(password, freshConfig);
      if (!isPasswordValid(currentValidations)) {
        setRespMessage('Password does not meet the required criteria');
        setIsError(true);
        // Update the config and validations in case they changed
        setPasswordConfig(freshConfig);
        setValidations(currentValidations);
        return;
      }

      setIsLoad(true);
      const payload = {
        new_pass: password,
      };
      const headers = { Authorization: 'Bearer ' + apiToken };
      const res = await Axios.patch(FORGOT_PASSWORD_UPDATE, payload, { headers });

      if (res) {
        let { response } = res.data;
        setRespMessage(response.msg);
        setIsError(false);
        clearState();
        history.push(AUTH_ROUTES.login);
      }
    } catch (err) {
      console.log(`err`, err);
      setRespMessage('Please correct your email address and try again.  If your email address is correct,  please contact your company’s Helium Administrator to request access');
      setIsError(true);
    } finally {
      setIsLoad(false);
    }
  };

  return (
    <section className="login-form-container">
      <article className="login-page">
        <picture className="site-logo">
          <Logo width={'450px'} height={'450px'} />
        </picture>
        <form className="login-form" onSubmit={submitPassword} style={{ background: '#f1f1f1' }}>
          <div className="card mb-0 border-0 shadow-0">
            <div className="card-body center-class">
              <div className="text-center mb-1">
                <h5 className={password ? 'mb-0' : 'mb-4'}>Please create your password</h5>
                <span className="d-block text-muted"></span>
              </div>
              {isError === null ? (
                ''
              ) : isError ? (
                <div className="alert alert-info alert-styled-right alert-dismissible">
                  <button type="button" className="close" data-dismiss="alert">
                    <span>×</span>
                  </button>
                  <span className="font-weight-semibold">{respMessage}</span>
                </div>
              ) : (
                <div className="alert alert-success alert-styled-right alert-dismissible">
                  <button type="button" className="close" data-dismiss="alert">
                    <span>×</span>
                  </button>
                  <span className="font-weight-semibold">{respMessage}</span>
                </div>
              )}
              <div className="form-group form-group-feedback form-group-feedback-right mb-2">
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="form-control"
                  value={password}
                  onChange={handlePassword}
                  placeholder="New Password"
                />
                <div className="form-control-feedback" onClick={handleShowPassword}>
                  {showPassword && (
                    <img src="/assets/svg-icons/bi-eye.svg" alt="bi-eye" className="eye-icon" />
                  )}
                  {!showPassword && (
                    <img
                      src="/assets/svg-icons/bi-eye-slash.svg"
                      alt="bi-eye"
                      className="eye-icon"
                    />
                  )}
                </div>
              </div>

              {renderPasswordRequirements()}

              <div className="form-group form-group-feedback form-group-feedback-right">
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="form-control"
                  value={confirmPassword}
                  onChange={handleConfirmPassword}
                  placeholder="Confirm New Password"
                />
                {matchPasswordError && (
                  <span className="password-match-error font-weight-bold">
                    x Both Password must match!
                  </span>
                )}
              </div>
              <button
                type="submit"
                className="btn btn-primary btn-block"
                onClick={submitPassword}
                disabled={isLoad || !isPasswordValid(validations)}
              >
                Reset password
                <i
                  className={isLoad ? 'icon-spinner2 spinner ml-2' : 'icon-paperplane ml-2'}
                ></i>{' '}
              </button>
            </div>
          </div>
        </form>
      </article>
    </section>
  );
};

export default UpdatePassword;
